// ---------------------------------------------------------------------------
// GroupSearch — Provider-aware AAD/App security group search component.
//
// Left dropdown toggles between AAD Group (search-by-name, auto-fill ID)
// and App/Service (manual name + App ID entry) modes.
//
// Used wherever a security group needs to be added to a role assignment —
// PermissionsPage (via TenantRoleSection) and other panels.
// ---------------------------------------------------------------------------

import {
  Button,
  Combobox,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Option,
  Spinner,
} from '@fluentui/react-components';
import {
  Add16Regular,
  Apps16Regular,
  ChevronDown16Regular,
  Search16Regular,
} from '@fluentui/react-icons';
import { useCallback, useMemo, useState } from 'react';

import {
  GROUP_SEARCH_AAD_LABEL,
  GROUP_SEARCH_AAD_PLACEHOLDER,
  GROUP_SEARCH_ADD_BUTTON,
  GROUP_SEARCH_APP_ID_PLACEHOLDER,
  GROUP_SEARCH_APP_LABEL,
  GROUP_SEARCH_APP_NAME_PLACEHOLDER,
  GROUP_SEARCH_GROUP_ID_HINT,
  GROUP_SEARCH_PROVIDER_AAD,
  GROUP_SEARCH_PROVIDER_APP,
} from '../../constants/index.js';
import { useAadGroupSearch } from '../../hooks/useAadGroupSearch/useAadGroupSearch.js';
import type { AadGroupInfo } from '../../services/accessControlService.interface.js';

import type {
  GroupSearchProps,
  GroupSearchProvider,
} from './GroupSearch.interface.js';
import { useGroupSearchStyles } from './GroupSearch.style.js';

export function GroupSearch({ service, onAdd }: GroupSearchProps) {
  const styles = useGroupSearchStyles();

  const [provider, setProvider] = useState<GroupSearchProvider>(
    GROUP_SEARCH_PROVIDER_AAD,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<AadGroupInfo | null>(null);
  const [appId, setAppId] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const { groups, isLoading: isSearching } = useAadGroupSearch({
    query: provider === GROUP_SEARCH_PROVIDER_AAD ? searchQuery : '',
    service,
  });

  const groupByDisplayName = useMemo(
    () => new Map(groups.map((g) => [g.displayName, g])),
    [groups],
  );

  const handleProviderChange = useCallback((next: GroupSearchProvider) => {
    setProvider(next);
    setSearchQuery('');
    setSelectedGroup(null);
    setAppId('');
    setAddError(null);
  }, []);

  const handleOptionSelect = useCallback(
    (_: unknown, data: { optionValue?: string }) => {
      const group = data.optionValue
        ? groupByDisplayName.get(data.optionValue)
        : undefined;
      setSelectedGroup(group ?? null);
      setAddError(null);
    },
    [groupByDisplayName],
  );

  const handleAdd = useCallback(async () => {
    setIsAdding(true);
    setAddError(null);
    try {
      let acg;
      if (provider === GROUP_SEARCH_PROVIDER_AAD && selectedGroup !== null) {
        acg = await service.createAccessControlGroup({
          sgGroupId: selectedGroup.groupId,
          provider: GROUP_SEARCH_PROVIDER_AAD,
          displayName: selectedGroup.displayName,
        });
      } else {
        acg = await service.createAccessControlGroup({
          sgGroupId: appId.trim(),
          provider: GROUP_SEARCH_PROVIDER_APP,
          displayName:
            searchQuery.trim() !== '' ? searchQuery.trim() : undefined,
        });
      }
      onAdd(acg);
      setSearchQuery('');
      setSelectedGroup(null);
      setAppId('');
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add group.');
    } finally {
      setIsAdding(false);
    }
  }, [provider, selectedGroup, appId, searchQuery, service, onAdd]);

  const isAddEnabled =
    !isAdding &&
    (provider === GROUP_SEARCH_PROVIDER_AAD
      ? selectedGroup !== null
      : appId.trim().length > 0);

  const groupIdDisplay =
    selectedGroup !== null ? selectedGroup.groupId : GROUP_SEARCH_GROUP_ID_HINT;

  const currentProviderLabel =
    provider === GROUP_SEARCH_PROVIDER_AAD
      ? GROUP_SEARCH_AAD_LABEL
      : GROUP_SEARCH_APP_LABEL;

  return (
    <div>
      <div className={styles.pickerCard}>
        {/* Provider selector */}
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <button
              className={styles.providerButton}
              aria-label={`Provider: ${currentProviderLabel}`}
              disabled={isAdding}
              type="button"
            >
              {provider === GROUP_SEARCH_PROVIDER_AAD ? (
                <Search16Regular aria-hidden="true" />
              ) : (
                <Apps16Regular aria-hidden="true" />
              )}
              <span className={styles.providerLabel}>
                {currentProviderLabel}
              </span>
              <ChevronDown16Regular aria-hidden="true" />
            </button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem
                icon={<Search16Regular />}
                onClick={() => handleProviderChange(GROUP_SEARCH_PROVIDER_AAD)}
              >
                {GROUP_SEARCH_AAD_LABEL}
              </MenuItem>
              <MenuItem
                icon={<Apps16Regular />}
                onClick={() => handleProviderChange(GROUP_SEARCH_PROVIDER_APP)}
              >
                {GROUP_SEARCH_APP_LABEL}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>

        {/* Input area — AAD mode: combobox + auto-filled ID; App mode: two inputs */}
        <div className={styles.inputSide}>
          {provider === GROUP_SEARCH_PROVIDER_AAD ? (
            <>
              <Combobox
                className={styles.combobox}
                placeholder={GROUP_SEARCH_AAD_PLACEHOLDER}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedGroup(null);
                }}
                onOptionSelect={handleOptionSelect}
                disabled={isAdding}
                aria-label={GROUP_SEARCH_AAD_LABEL}
                listbox={{ style: { maxHeight: '200px', overflowY: 'auto' } }}
              >
                {isSearching ? (
                  <Option key="loading" value="" disabled>
                    Searching...
                  </Option>
                ) : groups.length === 0 && searchQuery.trim().length > 0 ? (
                  <Option key="empty" value="" disabled>
                    No groups found
                  </Option>
                ) : (
                  groups.map((g) => (
                    <Option key={g.groupId} value={g.displayName}>
                      {g.displayName}
                    </Option>
                  ))
                )}
              </Combobox>
              <span className={styles.groupIdHint} aria-live="polite">
                {groupIdDisplay}
              </span>
            </>
          ) : (
            <>
              <Input
                className={styles.appInput}
                placeholder={GROUP_SEARCH_APP_NAME_PLACEHOLDER}
                value={searchQuery}
                onChange={(_, d) => setSearchQuery(d.value)}
                disabled={isAdding}
                aria-label="App name"
                size="small"
              />
              <Input
                className={styles.appInput}
                placeholder={GROUP_SEARCH_APP_ID_PLACEHOLDER}
                value={appId}
                onChange={(_, d) => setAppId(d.value)}
                disabled={isAdding}
                aria-label="App ID"
                size="small"
              />
            </>
          )}
        </div>

        {/* Add button */}
        <Button
          className={styles.addButton}
          appearance="secondary"
          icon={isAdding ? <Spinner size="extra-tiny" /> : <Add16Regular />}
          disabled={!isAddEnabled}
          onClick={() => {
            void handleAdd();
          }}
          aria-label={GROUP_SEARCH_ADD_BUTTON}
        >
          {GROUP_SEARCH_ADD_BUTTON}
        </Button>
      </div>

      {addError !== null && (
        <span className={styles.errorText} role="alert">
          {addError}
        </span>
      )}
    </div>
  );
}
